import React, { useState, useEffect } from 'react'
import { X, AlertCircle, Info } from 'lucide-react'
import clsx from 'clsx'

// Available instruction types
const instructionTypeOptions = [
  { value: 'translation', label: 'Translation', description: 'General translation prompt' },
  { value: 'translation_review', label: 'Review', description: 'Translation quality review prompt' },
  { value: 'translation_anthropic', label: 'Translation (Anthropic)', description: 'Anthropic-specific translation prompt' },
  { value: 'translation_gemini', label: 'Translation (Gemini)', description: 'Gemini-specific translation prompt' },
  { value: 'translation_test', label: 'Translation (Test)', description: 'Test/debug translation prompt' },
]

// Template variables
const templateVariables = [
  { name: '{source_lang}', description: 'Source language name (e.g., "English")' },
  { name: '{target_lang}', description: 'Target language name (e.g., "Czech")' },
  { name: '{context}', description: 'Translation context provided by user' },
  { name: '{batch_text}', description: 'Batch of texts to translate, separated by |||' },
  { name: '{pairs}', description: 'For review: original and translated text pairs' },
]

const InstructionEditModal = ({ instruction, onSubmit, onClose }) => {
  const isEditing = !!instruction

  const [formData, setFormData] = useState({
    type: instruction?.type || 'translation',
    instructions: instruction?.instructions || '',
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset form when instruction changes
  useEffect(() => {
    if (instruction) {
      setFormData({
        type: instruction.type,
        instructions: instruction.instructions,
      })
    } else {
      setFormData({
        type: 'translation',
        instructions: '',
      })
    }
    setErrors({})
  }, [instruction])

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when field changes
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
  }

  const insertVariable = (variable) => {
    const textarea = document.getElementById('instructions-textarea')
    if (textarea) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const text = formData.instructions
      const newText = text.substring(0, start) + variable + text.substring(end)
      handleChange('instructions', newText)
      // Set cursor position after inserted variable
      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start + variable.length, start + variable.length)
      }, 0)
    } else {
      handleChange('instructions', formData.instructions + variable)
    }
  }

  const validate = () => {
    const newErrors = {}

    if (!formData.type) {
      newErrors.type = 'Type is required'
    }

    if (!formData.instructions?.trim()) {
      newErrors.instructions = 'Instructions are required'
    } else if (formData.instructions.length < 20) {
      newErrors.instructions = 'Instructions must be at least 20 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    try {
      setIsSubmitting(true)
      await onSubmit(formData)
    } catch (err) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isEditing ? 'Edit Instruction' : 'New Instruction'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="px-6 py-4 space-y-4">
              {/* Type select */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Instruction Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  className={clsx(
                    'block w-full px-3 py-2 rounded-md shadow-sm text-sm',
                    'border bg-white dark:bg-gray-700',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500',
                    errors.type
                      ? 'border-red-300 dark:border-red-600'
                      : 'border-gray-300 dark:border-gray-600'
                  )}
                >
                  {instructionTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label} - {opt.description}
                    </option>
                  ))}
                </select>
                {errors.type && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.type}
                  </p>
                )}
              </div>

              {/* Instructions textarea */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  System Prompt *
                </label>
                <textarea
                  id="instructions-textarea"
                  value={formData.instructions}
                  onChange={(e) => handleChange('instructions', e.target.value)}
                  rows={12}
                  placeholder="Enter the instruction prompt for the LLM model..."
                  className={clsx(
                    'block w-full px-3 py-2 rounded-md shadow-sm text-sm font-mono',
                    'border bg-white dark:bg-gray-700',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500',
                    'placeholder:text-gray-400',
                    errors.instructions
                      ? 'border-red-300 dark:border-red-600'
                      : 'border-gray-300 dark:border-gray-600'
                  )}
                />
                {errors.instructions && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.instructions}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {formData.instructions.length} characters
                </p>
              </div>

              {/* Template variables */}
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Info className="w-4 h-4 text-blue-500 mr-2" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Insert Template Variable
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {templateVariables.map(v => (
                    <button
                      key={v.name}
                      type="button"
                      onClick={() => insertVariable(v.name)}
                      className={clsx(
                        'px-2 py-1 text-xs font-mono rounded',
                        'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
                        'hover:bg-blue-200 dark:hover:bg-blue-900/60',
                        'transition-colors'
                      )}
                      title={v.description}
                    >
                      {v.name}
                    </button>
                  ))}
                </div>
                <div className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
                  {templateVariables.map(v => (
                    <div key={v.name} className="flex">
                      <code className="font-mono text-gray-600 dark:text-gray-300 w-28">{v.name}</code>
                      <span>{v.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <button
                type="button"
                onClick={onClose}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-md',
                  'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700',
                  'border border-gray-300 dark:border-gray-600',
                  'hover:bg-gray-50 dark:hover:bg-gray-600',
                  'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                )}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-md',
                  'text-white bg-primary-600 hover:bg-primary-700',
                  'border border-transparent',
                  'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Instruction'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default InstructionEditModal
